import React, { useMemo } from 'react'
import { BookingCustomField } from '@xeetr/types/models'
import { useCustomFieldEditing } from '@/hooks/useCustomFieldEditing'

export interface SortableCustomFieldContainerProps {
  field: BookingCustomField
  index: number
  isEditing: boolean
  onEditStart: (index: number) => void
  onEditCancel: () => void
  onSave: (index: number, updatedField: BookingCustomField) => void
  onDelete: (index: number) => void
  children: (props: {
    field: BookingCustomField
    index: number
    isEditing: boolean
    editingState: ReturnType<typeof useCustomFieldEditing>
  }) => React.ReactNode
}

const SortableCustomFieldContainer: React.FC<SortableCustomFieldContainerProps> = ({
  field,
  index,
  isEditing,
  onEditStart,
  onEditCancel,
  onSave,
  onDelete,
  children,
}) => {
  const editingState = useCustomFieldEditing({
    field,
    onSave: (updatedField) => onSave(index, updatedField),
    onCancel: onEditCancel,
    onDelete: () => onDelete(index),
  })

  // Enhanced edit handler that also notifies parent
  const handleEditWithNotification = () => {
    editingState.handleEdit()
    onEditStart(index)
  }

  const enhancedEditingState = useMemo(() => ({
    ...editingState,
    handleEdit: handleEditWithNotification,
  }), [editingState, handleEditWithNotification])

  return (
    <>
      {children({
        field,
        index,
        isEditing,
        editingState: enhancedEditingState,
      })}
    </>
  )
}

export default SortableCustomFieldContainer